import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { authStateAtom, isSigningInAtom, isSigningOutAtom, isAuthenticatedAtom, userAtom, accessTokenAtom, authErrorAtom, needsMfaEnrollmentAtom, clearAuthStateAtom, clearAuthErrorAtom } from '../atoms/authAtoms.js';
import { mfaChallengeStateAtom, startMfaChallengeAtom, clearMfaChallengeAtom } from '../atoms/mfaChallengeAtoms.js';
import { clearMfaSmsStateAtom } from '../atoms/mfaSmsAtoms.js';
import { useAuthContext } from '../providers/AuthContext.js';

/**
 * Hook for managing authentication state and operations
 * Provides functions to sign in, sign out, and manage auth state.
 *
 * Note: Firebase auth listener + /me loading is handled by AuthSessionProvider.
 */
const useAuth = () => {
    const { authService, apiClient, queryClient } = useAuthContext();
    const [authState] = useAtom(authStateAtom);
    const [isSigningIn, setIsSigningIn] = useAtom(isSigningInAtom);
    const [isSigningOut, setIsSigningOut] = useAtom(isSigningOutAtom);
    // MFA challenge state
    const mfaChallengeState = useAtomValue(mfaChallengeStateAtom);
    const startMfaChallenge = useSetAtom(startMfaChallengeAtom);
    const clearMfaChallenge = useSetAtom(clearMfaChallengeAtom);
    const clearMfaSmsState = useSetAtom(clearMfaSmsStateAtom);
    const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
    const setUser = useSetAtom(userAtom);
    const setAccessToken = useSetAtom(accessTokenAtom);
    const setAuthError = useSetAtom(authErrorAtom);
    const setNeedsMfaEnrollment = useSetAtom(needsMfaEnrollmentAtom);
    const clearAuthState = useSetAtom(clearAuthStateAtom);
    const clearAuthError = useSetAtom(clearAuthErrorAtom);
    const refreshToken = useCallback(async () => {
        try {
            const token = await authService.getCurrentUserToken(true);
            if (token) {
                setAccessToken(token);
                apiClient.setAuthToken(token);
                return token;
            }
            return null;
        }
        catch (error) {
            console.error('Failed to refresh token:', error);
            return null;
        }
    }, [authService, apiClient, setAccessToken]);
    /**
     * Check if user needs MFA enrollment and update the atom accordingly.
     * A user needs MFA enrollment if they have no enrolled factors.
     */
    const checkAndSetMfaEnrollmentStatus = useCallback((user) => {
        const multiFactorUser = user
            .multiFactor;
        const enrolledFactors = multiFactorUser?.enrolledFactors ?? [];
        setNeedsMfaEnrollment(enrolledFactors.length === 0);
    }, [setNeedsMfaEnrollment]);
    const signIn = useCallback(async (credentials) => {
        setIsSigningIn(true);
        clearAuthError();
        try {
            const result = await authService.signIn(credentials);
            // Check if MFA challenge is required
            if (authService.isMfaRequiredResponse(result)) {
                // Store the resolver for the MFA challenge flow
                startMfaChallenge(result.resolver);
                return { success: false, mfaRequired: true };
            }
            // Normal sign-in success
            const { user, accessToken } = result;
            // Check if user needs MFA enrollment (no enrolled factors)
            checkAndSetMfaEnrollmentStatus(user);
            // Optimistically update auth state; AuthSessionProvider will also sync onAuthStateChanged.
            setUser(user);
            setAccessToken(accessToken);
            setIsAuthenticated(true);
            apiClient.setAuthToken(accessToken);
            apiClient.setTokenRefreshCallback(refreshToken);
            return { success: true, user, accessToken };
        }
        catch (error) {
            setAuthError(error);
            throw error;
        }
        finally {
            setIsSigningIn(false);
        }
    }, [
        setIsSigningIn,
        clearAuthError,
        setUser,
        setAccessToken,
        setIsAuthenticated,
        setAuthError,
        checkAndSetMfaEnrollmentStatus,
        refreshToken,
        startMfaChallenge,
        authService,
        apiClient,
    ]);
    const signInWithCustomToken = useCallback(async (credentials) => {
        setIsSigningIn(true);
        clearAuthError();
        try {
            const { user, accessToken } = await authService.signInWithCustomToken(credentials);
            // Check if user needs MFA enrollment (no enrolled factors)
            // This is critical for magic link flows where MFA challenge is bypassed
            checkAndSetMfaEnrollmentStatus(user);
            setUser(user);
            setAccessToken(accessToken);
            setIsAuthenticated(true);
            apiClient.setAuthToken(accessToken);
            apiClient.setTokenRefreshCallback(refreshToken);
            return { user, accessToken };
        }
        catch (error) {
            setAuthError(error);
            throw error;
        }
        finally {
            setIsSigningIn(false);
        }
    }, [
        setIsSigningIn,
        clearAuthError,
        setUser,
        setAccessToken,
        setIsAuthenticated,
        setAuthError,
        checkAndSetMfaEnrollmentStatus,
        refreshToken,
        authService,
        apiClient,
    ]);
    const signOut = useCallback(async () => {
        setIsSigningOut(true);
        try {
            await authService.signOut();
            clearAuthState();
            clearMfaChallenge(); // Clear any pending MFA challenge state
            setNeedsMfaEnrollment(false); // Clear MFA enrollment state
            clearMfaSmsState(); // Clear MFA SMS enrollment state (phone number, verification ID)
            apiClient.removeAuthToken();
            apiClient.removeTokenRefreshCallback();
            queryClient.clear(); // Clear all TanStack Query cache to prevent stale data on re-login
        }
        catch (error) {
            setAuthError(error);
            throw error;
        }
        finally {
            setIsSigningOut(false);
        }
    }, [
        setIsSigningOut,
        clearAuthState,
        clearMfaChallenge,
        setNeedsMfaEnrollment,
        clearMfaSmsState,
        setAuthError,
        authService,
        apiClient,
        queryClient,
    ]);
    const clearError = useCallback(() => {
        clearAuthError();
    }, [clearAuthError]);
    const needsMfaEnrollment = useAtomValue(needsMfaEnrollmentAtom);
    return {
        // State
        ...authState,
        isSigningIn,
        isSigningOut,
        // MFA challenge state
        mfaPending: mfaChallengeState.isPending,
        mfaResolver: mfaChallengeState.resolver,
        mfaSelectedMethod: mfaChallengeState.selectedMethod,
        mfaPhoneVerificationId: mfaChallengeState.phoneVerificationId,
        mfaEnrolledFactors: mfaChallengeState.enrolledFactors,
        availableMfaMethods: mfaChallengeState.availableMethods,
        // MFA enrollment state
        needsMfaEnrollment,
        // Actions
        signIn,
        signInWithCustomToken,
        signOut,
        refreshToken,
        clearError,
        clearMfaChallenge,
    };
};

export { useAuth };
//# sourceMappingURL=useAuth.js.map
